import { useCallback, useMemo, useRef } from 'react';
import { serialize } from 'js/utils/url.util';

import { usePostCodeConfig } from './usePostCodeConfig';
import { POSTCODE_PROVIDER_ERROR } from '../constants';
import { get, parseFind, parseRetrieve } from '../helpers';

import * as Types from '../types';

export const usePostCodeSearch = () => {
    const abortController = useRef<AbortController | null>(null);
    const config = usePostCodeConfig();

    const search = useCallback(async ({
        searchText,
        countryCode,
        id
    }: {
        searchText: string;
        countryCode?: string;
        id?: string;
    }): Promise<Types.IParsedSearchItem[]> => {
        abortController.current?.abort();
        abortController.current = new AbortController();

        const country = countryCode?.toLowerCase() || '';
        const locationParams = countryCode
            ? { Origin: country, IsMiddleware: true }
            : { Countries: config.countries.join(',') };
        const params = {
            Text: searchText,
            Key: config.key,
            Language: config.language,
            Container: id,
            ...locationParams
        };
        const searchPart = serialize(params);

        try {
            const response = await get(`${config.searchUrl}?${searchPart}`, abortController.current.signal);

            return await parseFind(response);
        } catch (error: Types.IParsedPostCodeError | any) {
            if (error.errorCode === POSTCODE_PROVIDER_ERROR.ABORT_ERROR) {
                return [];
            }

            throw error;
        }
    }, [config]);

    const retrieve = useCallback(async ({ id }: { id: string }): Promise<Types.IParsedRetrieveItem[]> => {
        abortController.current?.abort();
        abortController.current = new AbortController();

        const params = {
            Key: config.key,
            Id: id
        };
        const searchPart = serialize(params);

        try {
            const response = await get(`${config.retrieveUrl}?${searchPart}`, abortController.current.signal);

            return await parseRetrieve(response);
        } catch (error: any) {
            if (error.errorCode === POSTCODE_PROVIDER_ERROR.ABORT_ERROR) {
                return [];
            }

            throw error;
        }
    }, [config]);

    return useMemo(() => ({
        search, retrieve
    }), [search, retrieve]);
};
